/**
 * Anzeige-Helpers für Termine im Kalender (06/2026 Refactor Phase 1).
 *
 * Pure-functions: keine Hooks, kein State. Alle Daten kommen über Argumente.
 */

import { getSwissHM } from '@/components/appointments/calendarUtils';

/**
 * Hex-Farbe in `rgba(r,g,b,alpha)` umwandeln — wird benötigt um „bereits
 * abgerechnete" Termine mit reduzierter Opazität zu zeigen.
 */
const hexToRgba = (hex, alpha) => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

/**
 * Liefert die Farbe für einen Termin gemäss Status-Hierarchie:
 * - Chatbot-Reservation: Amber
 * - Eingecheckt / Completed: paid-Farbe
 * - billing_status='billed': paid mit Transparenz
 * - In der Vergangenheit ohne Check-in: overdue-Farbe
 * - Sonst: Service-Farbe oder pending-Farbe
 */
export const getAppointmentColor = (appointment, services = [], paymentColors = {}, customerId = null) => {
  if (appointment._isReservation) {
    return '#F59E0B'; // amber-500 — Chatbot-Reservation
  }

  // Abgesagt / nicht erschienen (nur sichtbar wenn "Abgesagte anzeigen" aktiv)
  // → neutrales Grau, klar abgegrenzt von aktiven/überfälligen Terminen.
  if (appointment.status === 'cancelled' || appointment.status === 'no_show') {
    return '#9CA3AF'; // gray-400
  }

  // FLEX-Fix 06/2026: Bei Mehr-Teilnehmer-Terminen den Status PRO Teilnehmer
  // färben. So wird nur der eingecheckte/abgerechnete Teilnehmer grün, nicht
  // automatisch alle. Basiert auf participants_billing[customerId] /
  // checked_in_customer_ids.
  if (customerId && (appointment.customer_ids || []).length > 1) {
    const pb = (appointment.participants_billing || {})[customerId] || {};
    const checkedInIds = appointment.checked_in_customer_ids || [];
    if (pb.checked_in || checkedInIds.includes(customerId) || pb.billing_status === 'paid') {
      return paymentColors.paid || '#10B981';
    }
    if (pb.billing_status === 'billed') {
      return hexToRgba(paymentColors.paid || '#10B981', 0.7);
    }
    // Dieser Teilnehmer noch offen → nicht grün, weiter mit normaler Logik
    // (überfällig/pending), aber NICHT den Termin-Gesamtstatus als grün werten.
    const nowP = new Date();
    if (new Date(appointment.start_time) < nowP) {
      return paymentColors.overdue || '#EF4444';
    }
    const svcP = services.find((s) => s.id === appointment.service_id);
    return svcP?.color || paymentColors.pending || '#FCD34D';
  }

  if (appointment.checked_in || appointment.status === 'completed') {
    return paymentColors.paid || '#10B981';
  }

  // Über Rechnung bezahlt (z.B. Ende-Monats-Abrechnung) → grün, auch ohne Check-in
  if (appointment.billing_status === 'paid') {
    return paymentColors.paid || '#10B981';
  }

  if (appointment.billing_status === 'billed') {
    return hexToRgba(paymentColors.paid || '#10B981', 0.7);
  }

  const now = new Date();
  const aptStart = new Date(appointment.start_time);
  if (aptStart < now) {
    return paymentColors.overdue || '#EF4444';
  }

  const service = services.find((s) => s.id === appointment.service_id);
  return service?.color || paymentColors.pending || '#FCD34D';
};

/**
 * Weisse Schrift auf roten (überfälligen) Terminen — schwarz auf allen anderen
 * Farben (06/2026, User-Wunsch: schwarze Schrift ist auf Rot unlesbar).
 */
export const getAppointmentTextColor = (bgColor) => {
  const c = String(bgColor || '').toLowerCase().replace(/\s/g, '');
  if (c === '#ef4444' || c === '#dc2626' || c.startsWith('rgba(239,68,68')) {
    return '#FFFFFF';
  }
  return '#000000';
};

/**
 * @deprecated nur noch für Legacy-Pfade vorhanden — neue Logik nutzt
 * `getAppointmentColor`.
 */
export const getPaymentStatusColor = (paymentStatus) => {
  switch (paymentStatus) {
    case 'pending':
      return '#FCD34D';
    case 'paid':
      return '#10B981';
    case 'overdue':
      return '#EF4444';
    default:
      return '#3B82F6';
  }
};

/** Lesbares Label für die Zahlungsart-Badge im Termin-Kärtchen. */
export const getPaymentBadge = (paymentMethod) => {
  switch (paymentMethod) {
    case 'points':
      return 'Punkte';
    case 'card':
      return 'Karte';
    case 'twint':
      return 'Twint';
    default:
      return '';
  }
};

/** Höhe (in Pixel) eines Termins basierend auf seiner Dauer in 15-Min-Slots. */
export const calculateAppointmentHeight = (appointment) => {
  const duration = appointment.duration_minutes || 30;
  const slots = duration / 15;
  return slots * 48; // 48 px je Slot
};

/**
 * Prüft ob `appointment` in dem 30-Minuten-Slot `timeSlot` (HH:MM) startet.
 * Wird gebraucht, um den Termin NUR EINMAL pro Tag zu rendern (im ersten Slot,
 * den er belegt) — alle weiteren Zellen darunter werden zugedeckt.
 */
export const isFirstSlotOfAppointment = (appointment, timeSlot) => {
  if (!appointment) return false;
  const [slotHours, slotMinutes] = timeSlot.split(':').map(Number);
  const slotStartTime = slotHours * 60 + slotMinutes;
  const slotEndTime = slotStartTime + 30;
  const sStart = getSwissHM(appointment.start_time);
  const aptStartMinutes = sStart.hour * 60 + sStart.minute;
  return aptStartMinutes >= slotStartTime && aptStartMinutes < slotEndTime;
};
